// Browser error reporting to POST /telemetry/errors (no session needed; the Worker redacts, rate limits
// and groups them into issues). Deduped per browser session, at most 10 reports per page load.

export type ErrorKind = 'error' | 'unhandledrejection' | 'route'

export interface ErrorReport {
  kind: ErrorKind
  type?: string
  message: string
  stack?: string
  /** Path only: no query or hash, which can carry tokens. */
  url: string
}

export const ENDPOINT = '/telemetry/errors'
export const MAX_PER_PAGE = 10
const SESSION_KEY = 'lobby:reported-errors'

/** Noise without information: cross-origin script errors and benign browser warnings. */
const IGNORED = [/^Script error\.?$/, /ResizeObserver loop/]

let sentThisPage = 0
const seenThisPage = new Set<string>()

/** Normalises anything thrown into a report, or null when it is not worth sending. */
export function toReport(kind: ErrorKind, error: unknown, path: string): ErrorReport | null {
  let type: string | undefined
  let message: string
  let stack: string | undefined
  if (error instanceof Error) {
    // Expected API failures (4xx, offline) are handled in the UI.
    if (error.name === 'ApiError') return null
    type = error.name
    message = error.message
    stack = error.stack
  } else if (typeof error === 'string') {
    message = error
  } else {
    try {
      message = JSON.stringify(error) ?? String(error)
    } catch {
      message = String(error)
    }
  }
  if (!message || IGNORED.some((re) => re.test(message))) return null
  return {
    kind,
    ...(type ? { type: type.slice(0, 100) } : {}),
    message: message.slice(0, 1500),
    ...(stack ? { stack: stack.slice(0, 5000) } : {}),
    url: path.split(/[?#]/)[0]!.slice(0, 500),
  }
}

function seenInSession(key: string): boolean {
  if (seenThisPage.has(key)) return true
  seenThisPage.add(key)
  try {
    const list = JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]') as string[]
    if (list.includes(key)) return true
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...list, key].slice(-50)))
  } catch {
    // Storage blocked: the per-page set still dedupes.
  }
  return false
}

/** Sends one report unless it is a duplicate or the page budget is used up. Returns whether it was sent. */
export function reportError(error: unknown, kind: ErrorKind = 'error'): boolean {
  if (sentThisPage >= MAX_PER_PAGE || typeof window === 'undefined') return false
  const report = toReport(kind, error, window.location.pathname)
  if (!report) return false
  const key = `${report.type ?? ''}|${report.message}|${report.stack?.split('\n')[1]?.trim() ?? ''}`.slice(0, 300)
  if (seenInSession(key)) return false
  sentThisPage++
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(report),
    }).catch(() => {})
  } catch {
    // Reporting must never break the page.
  }
  return true
}

/** Listens for uncaught errors and unhandled promise rejections. Returns an uninstall function. */
export function installErrorReporter(target: Window = window): () => void {
  const onError = (e: ErrorEvent) => void reportError(e.error ?? e.message, 'error')
  const onRejection = (e: PromiseRejectionEvent) => void reportError(e.reason, 'unhandledrejection')
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}

/** Tests only. */
export function resetErrorReporter() {
  sentThisPage = 0
  seenThisPage.clear()
}
